import { useEffect, useState } from "react";
import { X, Loader2, Plus, Check, Trash2, Power, AlertTriangle, Building2, Wrench, Radio, ShieldCheck, ShieldAlert, CreditCard, PhoneCall, Zap, Activity, Settings2, ExternalLink, ListChecks, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fetchAdminCompany, fetchAdminPricingCatalog, formatApiError, patchAdminCompanyNativeFlag, patchAdminIntegrationElevenLabs, manualBlockAdminIntegration, manualReopenAdminIntegration } from "@/lib/api";
import AdminIntegrationTools from "@/components/admin/AdminIntegrationTools";
import AdminBrowserTestCard from "@/components/admin/AdminBrowserTestCard";
import DeleteCompanyDialog from "@/components/admin/DeleteCompanyDialog";

const fmtDKK = (v) => new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(v || 0) + " kr.";

/**
 * CompanyDetailModal — three tabs:
 *  1. Firma & kontakt (company profile + primary contact)
 *  2. Fagområder & opgavetyper
 *  3. Integrationer (read-only ElevenLabs status, no secrets)
 * Uses PATCH-partial to only send changed fields.
 */
export default function CompanyDetailModal({ open, companyId, catalog, onClose, onSubmit, onUpdated, onDeleted }) {
    const [initial, setInitial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("firma"); // firma | fagomraader | integrationer
    const [deleteOpen, setDeleteOpen] = useState(false);

    // profile
    const [name, setName] = useState("");
    const [cvr, setCvr] = useState("");
    const [address, setAddress] = useState("");
    const [postal, setPostal] = useState("");
    const [city, setCity] = useState("");
    const [contactPerson, setContactPerson] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [plan, setPlan] = useState("");
    const [subStatus, setSubStatus] = useState("none");
    const [active, setActive] = useState(true);

    // trades
    const [enabledTrades, setEnabledTrades] = useState([]);
    const [primary, setPrimary] = useState("");
    const [categories, setCategories] = useState({});
    const [newCatInput, setNewCatInput] = useState({});

    // subscription
    const [sub, setSub] = useState({
        plan: null, extra_customer_flows: 0, extra_employee_flows: 0,
        minute_blocks: 0, command_blocks: 0, agreement_period: "monthly",
    });
    const [pricing, setPricing] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const d = await fetchAdminCompany(companyId);
            setInitial(d);
            setName(d.name || "");
            setCvr(d.cvr || "");
            setAddress(d.address || "");
            setPostal(d.postal_code || "");
            setCity(d.city || "");
            setContactPerson(d.contact_person || "");
            setContactEmail(d.contact_email || "");
            setContactPhone(d.contact_phone || "");
            setPlan(d.plan || "");
            setSubStatus(d.subscription_status || "none");
            setActive(d.active !== false);
            setEnabledTrades(d.enabled_trades || []);
            setPrimary(d.primary_trade || "");
            setCategories(d.job_categories_by_trade || {});
            setSub(d.subscription || {
                plan: null, extra_customer_flows: 0, extra_employee_flows: 0,
                minute_blocks: 0, command_blocks: 0, agreement_period: "monthly",
            });
            setError("");
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        fetchAdminPricingCatalog().then(setPricing).catch(() => {});
    }, [open]);

    useEffect(() => {
        if (!open || !companyId) return;
        reload();
    }, [open, companyId]);

    if (!open) return null;

    const toggleTrade = (id) => {
        setEnabledTrades((prev) => {
            const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
            if (primary && !next.includes(primary)) setPrimary(next[0] || "");
            return next;
        });
    };

    const addCategory = (trade) => {
        const val = (newCatInput[trade] || "").trim();
        if (!val) return;
        setCategories((prev) => {
            const cur = prev[trade] || [];
            if (cur.some((c) => c.toLowerCase() === val.toLowerCase())) return prev;
            return { ...prev, [trade]: [...cur, val] };
        });
        setNewCatInput((prev) => ({ ...prev, [trade]: "" }));
    };

    const removeCategory = (trade, idx) => {
        const cur = categories[trade] || [];
        if ((cur[idx] || "").toLowerCase() === "andet") {
            toast.info('"Andet" kan ikke fjernes — den er altid tilgængelig.');
            return;
        }
        setCategories((prev) => ({ ...prev, [trade]: (prev[trade] || []).filter((_, i) => i !== idx) }));
    };

    const changed = (key, cur) => JSON.stringify(initial?.[key]) !== JSON.stringify(cur);

    const buildPayload = () => {
        const p = {};
        if (name.trim() !== (initial?.name || "")) p.name = name.trim();
        if (cvr !== (initial?.cvr || "")) p.cvr = cvr;
        if (address !== (initial?.address || "")) p.address = address;
        if (postal !== (initial?.postal_code || "")) p.postal_code = postal;
        if (city !== (initial?.city || "")) p.city = city;
        if (contactPerson !== (initial?.contact_person || "")) p.contact_person = contactPerson;
        if (contactEmail !== (initial?.contact_email || "")) p.contact_email = contactEmail;
        if (contactPhone !== (initial?.contact_phone || "")) p.contact_phone = contactPhone;
        if ((plan || null) !== (initial?.plan || null)) p.plan = plan;
        if (subStatus !== (initial?.subscription_status || "none")) p.subscription_status = subStatus;
        if (Boolean(active) !== Boolean(initial?.active)) p.active = active;
        if (changed("enabled_trades", enabledTrades)) p.enabled_trades = enabledTrades;
        if ((initial?.primary_trade || "") !== primary) p.primary_trade = primary || null;
        if (changed("job_categories_by_trade", categories)) p.job_categories_by_trade = categories;
        if (changed("subscription", sub)) p.subscription = sub;
        return p;
    };

    const handleSave = async () => {
        const patch = buildPayload();
        if (Object.keys(patch).length === 0) { onClose?.(); return; }
        setSaving(true);
        try {
            await onSubmit(companyId, patch);
            onUpdated?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke gemme ændringer");
        } finally { setSaving(false); }
    };

    const el = initial?.elevenlabs || {};

    // Client-side preview economics — MUST mirror pricing.py exactly.
    // The truthful value comes from `initial.economics` after save.
    const previewEcon = (() => {
        if (!pricing) return null;
        const pkg = pricing.packages.find((p) => p.id === sub.plan);
        const addons = pricing.addons || {};
        const disc = pricing.agreements.find((a) => a.id === sub.agreement_period)?.discount_pct || 0;
        const lines = [];
        if (pkg) lines.push({ label: pkg.label, qty: 1, unit_price: pkg.price, unit_cost: pkg.cost });
        const push = (k, qty) => {
            if (qty > 0 && addons[k]) {
                lines.push({ label: addons[k].label, qty, unit_price: addons[k].price, unit_cost: addons[k].cost });
            }
        };
        push("extra_customer_flow", sub.extra_customer_flows);
        push("extra_employee_flow", sub.extra_employee_flows);
        push("minute_block", sub.minute_blocks);
        push("command_block", sub.command_blocks);
        const brutto = lines.reduce((s, l) => s + l.unit_price * l.qty, 0);
        const cost = lines.reduce((s, l) => s + l.unit_cost * l.qty, 0);
        const netto = Math.round(brutto * (1 - disc));
        return { lines, brutto, netto, cost, profit: netto - cost, discount_pct: disc };
    })();

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="kunde-detail-modal">
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-semibold text-slate-900 truncate" data-testid="kunde-detail-title">{name || "Virksomhed"}</h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {[initial?.subscription_status || "—", initial?.plan].filter(Boolean).join(" · ")}
                            {initial?.user_count > 0 && ` · ${initial.user_count} bruger${initial.user_count === 1 ? "" : "e"}`}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded shrink-0" data-testid="kunde-detail-close">
                        <X className="w-4 h-4 text-slate-500" />
                    </button>
                </div>

                {loading ? (
                    <div className="p-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
                ) : error ? (
                    <div className="p-6">
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
                            <AlertTriangle className="w-5 h-5 inline mr-2" /> {error}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="border-b border-slate-200 px-6">
                            <div className="flex gap-1">
                                <TabButton value="firma" tab={tab} onClick={setTab} icon={Building2} label="Firma & kontakt" />
                                <TabButton value="abonnement" tab={tab} onClick={setTab} icon={CreditCard} label="Abonnement" />
                                <TabButton value="fagomraader" tab={tab} onClick={setTab} icon={Wrench} label="Fagområder" />
                                <TabButton value="integrationer" tab={tab} onClick={setTab} icon={Radio} label="Integrationer" />
                            </div>
                        </div>

                        <div className="max-h-[65vh] overflow-y-auto p-6">
                            {tab === "firma" && (
                                <div className="space-y-6" data-testid="kunde-tab-firma">
                                    <ActiveToggle active={active} onChange={setActive} />
                                    <Section title="VIRKSOMHED">
                                        <TextField label="Virksomhedsnavn" value={name} onChange={setName} testid="kunde-edit-name" required />
                                        <div className="grid grid-cols-2 gap-3">
                                            <TextField label="CVR" value={cvr} onChange={setCvr} testid="kunde-edit-cvr" mono />
                                            <TextField label="Adresse" value={address} onChange={setAddress} testid="kunde-edit-address" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <TextField label="Postnummer" value={postal} onChange={setPostal} testid="kunde-edit-postal" />
                                            <TextField label="By" value={city} onChange={setCity} testid="kunde-edit-city" />
                                        </div>
                                    </Section>
                                    <Section title="PRIMÆR KONTAKT">
                                        <TextField label="Kontaktperson" value={contactPerson} onChange={setContactPerson} testid="kunde-edit-contact-person" />
                                        <div className="grid grid-cols-2 gap-3">
                                            <TextField label="Email" value={contactEmail} onChange={setContactEmail} testid="kunde-edit-contact-email" type="email" />
                                            <TextField label="Telefonnummer" value={contactPhone} onChange={setContactPhone} testid="kunde-edit-contact-phone" />
                                        </div>
                                    </Section>
                                    <Section title="ABONNEMENT">
                                        <div className="grid grid-cols-2 gap-3">
                                            <TextField label="Plan" value={plan} onChange={setPlan} testid="kunde-edit-plan" />
                                            <div>
                                                <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                                                <select value={subStatus} onChange={(e) => setSubStatus(e.target.value)}
                                                        data-testid="kunde-edit-sub-status"
                                                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm">
                                                    <option value="none">Ingen</option>
                                                    <option value="trialing">Prøveperiode</option>
                                                    <option value="active">Aktiv</option>
                                                    <option value="grandfathered">Grandfathered</option>
                                                    <option value="past_due">Forfalden</option>
                                                    <option value="canceled">Opsagt</option>
                                                </select>
                                            </div>
                                        </div>
                                    </Section>

                                    {/* Farlig zone — permanent sletning */}
                                    <div className="border-2 border-rose-200 rounded-xl p-4 bg-rose-50/40" data-testid="kunde-danger-zone">
                                        <div className="flex items-start gap-3 mb-3">
                                            <ShieldAlert className="w-5 h-5 text-rose-600 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-bold text-rose-900">Farlig zone</p>
                                                <p className="text-xs text-rose-800 mt-0.5">
                                                    Slet virksomheden permanent. Alle data, integrationer og brugere fjernes uigenkaldeligt.
                                                    Kræver en 6-cifret kode sendt til admin-mailen.
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setDeleteOpen(true)}
                                            data-testid="kunde-delete-open"
                                            className="inline-flex items-center gap-2 bg-white hover:bg-rose-100 text-rose-700 border border-rose-300 text-sm font-medium px-4 py-2 rounded-xl transition"
                                        >
                                            <Trash2 className="w-4 h-4" /> Slet virksomhed permanent
                                        </button>
                                    </div>
                                </div>
                            )}

                            {tab === "abonnement" && (
                                <div className="space-y-5" data-testid="kunde-tab-abonnement">
                                    <Section title="PAKKE">
                                        <div className="grid grid-cols-2 gap-3">
                                            {(pricing?.packages || []).map((p) => (
                                                <button key={p.id} type="button"
                                                    onClick={() => setSub((s) => ({ ...s, plan: s.plan === p.id ? null : p.id }))}
                                                    data-testid={`sub-plan-${p.id}`}
                                                    className={`text-left p-4 rounded-xl border-2 transition ${
                                                        sub.plan === p.id ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                                                    }`}>
                                                    <p className="text-xs uppercase tracking-wider text-slate-500">Pakke</p>
                                                    <p className="text-lg font-bold text-slate-900 mt-1">{p.label}</p>
                                                    <p className="text-sm text-slate-700 mt-1">{fmtDKK(p.price)}/md.</p>
                                                </button>
                                            ))}
                                        </div>
                                    </Section>

                                    <Section title="TILVALG PR. MÅNED">
                                        <AddonRow label="Ekstra kundeflow" unitPrice={2200}
                                            value={sub.extra_customer_flows}
                                            onChange={(v) => setSub((s) => ({ ...s, extra_customer_flows: v }))}
                                            testid="sub-extra-customer-flows" />
                                        <AddonRow label="Ekstra medarbejderflow" unitPrice={1900}
                                            value={sub.extra_employee_flows}
                                            onChange={(v) => setSub((s) => ({ ...s, extra_employee_flows: v }))}
                                            testid="sub-extra-employee-flows" />
                                        <AddonRow label="Blokke à 100 ekstra kundeminutter" unitPrice={600}
                                            value={sub.minute_blocks}
                                            onChange={(v) => setSub((s) => ({ ...s, minute_blocks: v }))}
                                            testid="sub-minute-blocks" />
                                        <AddonRow label="Blokke à 1.000 ekstra medarbejderkommandoer" unitPrice={400}
                                            value={sub.command_blocks}
                                            onChange={(v) => setSub((s) => ({ ...s, command_blocks: v }))}
                                            testid="sub-command-blocks" />
                                    </Section>

                                    <Section title="AFTALEPERIODE">
                                        <div className="grid grid-cols-3 gap-2">
                                            {(pricing?.agreements || []).map((a) => (
                                                <button key={a.id} type="button"
                                                    onClick={() => setSub((s) => ({ ...s, agreement_period: a.id }))}
                                                    data-testid={`sub-agreement-${a.id}`}
                                                    className={`px-3 py-2 rounded-xl border-2 text-sm transition ${
                                                        sub.agreement_period === a.id ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold" : "border-slate-200 hover:border-slate-300 text-slate-700"
                                                    }`}>
                                                    {a.label}
                                                    {a.discount_pct > 0 && <span className="ml-1 text-xs text-emerald-600">–{Math.round(a.discount_pct * 100)}%</span>}
                                                </button>
                                            ))}
                                        </div>
                                    </Section>

                                    {/* Live economics preview */}
                                    {previewEcon && (
                                        <div className="rounded-2xl border-2 border-slate-200 p-4 bg-slate-50" data-testid="sub-economics-preview">
                                            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">ØKONOMI PR. MÅNED</p>
                                            <div className="space-y-2 mb-3">
                                                {previewEcon.lines.map((l, i) => (
                                                    <div key={i} className="flex justify-between text-sm">
                                                        <span className="text-slate-600">{l.qty} × {l.label}</span>
                                                        <span className="font-mono text-slate-800">{fmtDKK(l.unit_price * l.qty)}</span>
                                                    </div>
                                                ))}
                                                {previewEcon.lines.length === 0 && (
                                                    <p className="text-xs text-slate-400 italic">Vælg en pakke for at se økonomi.</p>
                                                )}
                                            </div>
                                            <div className="border-t border-slate-200 pt-3 space-y-1.5 text-sm">
                                                <Row label="Brutto MRR" value={fmtDKK(previewEcon.brutto)} testid="econ-brutto" />
                                                {previewEcon.discount_pct > 0 && (
                                                    <Row label={`Aftalerabat (${Math.round(previewEcon.discount_pct * 100)}%)`}
                                                         value={`– ${fmtDKK(previewEcon.brutto - previewEcon.netto)}`}
                                                         valueClass="text-emerald-700" testid="econ-discount" />
                                                )}
                                                <Row label="Netto MRR" value={fmtDKK(previewEcon.netto)} bold testid="econ-netto" />
                                                <Row label="Kostpris" value={fmtDKK(previewEcon.cost)} valueClass="text-rose-700" testid="econ-cost" />
                                                <Row label="Dækningsbidrag" value={fmtDKK(previewEcon.profit)}
                                                     bold valueClass="text-blue-700 text-base" testid="econ-profit" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {tab === "fagomraader" && (
                                <div className="space-y-6" data-testid="kunde-tab-fagomraader">
                                    <Section title="AKTIVE FAGOMRÅDER">
                                        <div className="flex flex-wrap gap-2 mb-2">
                                            {catalog.map((t) => {
                                                const on = enabledTrades.includes(t.id);
                                                return (
                                                    <button key={t.id} type="button" onClick={() => toggleTrade(t.id)}
                                                            data-testid={`kunde-edit-trade-${t.id}`}
                                                            className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition ${
                                                                on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                                                            }`}>
                                                        {on ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                                                        {t.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {enabledTrades.length > 0 && (
                                            <div className="mt-3">
                                                <label className="block text-xs font-medium text-slate-500 mb-1">Primært fagområde</label>
                                                <select value={primary} onChange={(e) => setPrimary(e.target.value)}
                                                        data-testid="kunde-edit-primary"
                                                        className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm">
                                                    <option value="">— ingen —</option>
                                                    {catalog.filter((t) => enabledTrades.includes(t.id)).map((t) => (
                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        {enabledTrades.length === 0 && (
                                            <p className="text-xs text-amber-700 flex items-center gap-1 mt-2">
                                                <AlertTriangle className="w-3 h-3" /> Uden aktive fagområder afvises alle voice-intake kald.
                                            </p>
                                        )}
                                    </Section>

                                    {enabledTrades.length > 0 && (
                                        <Section title="OPGAVETYPER PR. FAGOMRÅDE">
                                            <p className="text-xs text-slate-500">
                                                Voice-intake accepterer kun opgaver hvis <em>job_category</em> matcher listen. {"\"Andet\""} er altid tilgængelig.
                                            </p>
                                            <div className="space-y-3">
                                                {enabledTrades.map((tid) => {
                                                    const trade = catalog.find((t) => t.id === tid);
                                                    const cats = categories[tid] || [];
                                                    return (
                                                        <div key={tid} className="border border-slate-200 rounded-xl p-3" data-testid={`kunde-edit-cat-group-${tid}`}>
                                                            <p className="text-sm font-medium text-slate-800 mb-2">{trade?.name || tid}</p>
                                                            <div className="flex flex-wrap gap-1.5 mb-2">
                                                                {cats.length === 0 && <span className="text-xs text-slate-400 italic">Kun {"\"Andet\""} er aktiv</span>}
                                                                {cats.map((c, i) => {
                                                                    const isAndet = (c || "").toLowerCase() === "andet";
                                                                    return (
                                                                        <span key={i}
                                                                              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                                                                                  isAndet ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-slate-100 text-slate-700"
                                                                              }`}>
                                                                            {c}{isAndet && <span title="Altid tilgængelig" className="text-amber-700 text-[10px]">🔒</span>}
                                                                            {!isAndet && (
                                                                                <button type="button" onClick={() => removeCategory(tid, i)}
                                                                                        data-testid={`kunde-edit-cat-remove-${tid}-${i}`}
                                                                                        className="text-slate-400 hover:text-red-600">
                                                                                    <Trash2 className="w-3 h-3" />
                                                                                </button>
                                                                            )}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <input type="text" value={newCatInput[tid] || ""}
                                                                       onChange={(e) => setNewCatInput((p) => ({ ...p, [tid]: e.target.value }))}
                                                                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(tid); } }}
                                                                       placeholder="Ny opgavetype…"
                                                                       data-testid={`kunde-edit-cat-input-${tid}`}
                                                                       className="flex-1 px-2 py-1 rounded-lg border border-slate-200 text-sm" />
                                                                <button type="button" onClick={() => addCategory(tid)}
                                                                        data-testid={`kunde-edit-cat-add-${tid}`}
                                                                        className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1 rounded-lg">
                                                                    <Plus className="w-3 h-3" /> Tilføj
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </Section>
                                    )}
                                </div>
                            )}

                            {tab === "integrationer" && (
                                <div className="space-y-4" data-testid="kunde-tab-integrationer">
                                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-start gap-3">
                                        <ShieldCheck className="w-4 h-4 text-slate-500 mt-0.5" />
                                        <div className="text-xs text-slate-600 flex-1">
                                            <strong className="text-slate-800">Read-only status.</strong> Secrets kan ikke ses her. Native-flag kan slås til/fra pr. virksomhed.
                                        </div>
                                    </div>
                                    <AdminBrowserTestCard
                                        el={el}
                                        company={initial}
                                        companyId={companyId}
                                        onReload={reload}
                                    />
                                    <IntegrationChecklist
                                        el={el}
                                        companyId={companyId}
                                        onUpdated={reload}
                                    />
                                    <ElevenLabsIntegrationCard
                                        el={el}
                                        company={initial}
                                        onReload={reload}
                                    />
                                    <AdminIntegrationTools
                                        companyId={companyId}
                                        onReload={reload}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
                            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800" data-testid="kunde-detail-cancel">Annullér</button>
                            <button onClick={handleSave} disabled={saving}
                                    data-testid="kunde-detail-save"
                                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-xl">
                                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Gem ændringer
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Permanent delete dialog — sibling so it can render above the parent modal */}
            <DeleteCompanyDialog
                open={deleteOpen}
                company={initial}
                onClose={() => setDeleteOpen(false)}
                onDeleted={(res) => {
                    setDeleteOpen(false);
                    onDeleted?.(res);
                    onClose?.();
                }}
            />
        </div>
    );
}

function TabButton({ value, tab, onClick, icon: Icon, label }) {
    const active = tab === value;
    return (
        <button onClick={() => onClick(value)}
                data-testid={`kunde-tab-btn-${value}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                    active ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}>
            <Icon className="w-4 h-4" />
            {label}
        </button>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</p>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function TextField({ label, value, onChange, testid, required, mono, type = "text" }) {
    return (
        <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">{label}{required && <span className="text-rose-600"> *</span>}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
                   data-testid={testid}
                   className={`w-full px-3 py-2 rounded-xl border border-slate-200 text-sm ${mono ? "font-mono" : ""}`} />
        </div>
    );
}

function ActiveToggle({ active, onChange }) {
    return (
        <button type="button" onClick={() => onChange(!active)}
                data-testid="kunde-edit-active-toggle"
                className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition ${
                    active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
                }`}>
            {active ? <><Check className="w-4 h-4" /> Aktiv</> : <><Power className="w-4 h-4" /> Deaktiveret — brugere spærres straks</>}
        </button>
    );
}

function StatusRow({ label, value, sub, tone, mono, testid }) {
    const toneCls = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-700" : "text-slate-800";
    return (
        <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
            <span className="text-xs text-slate-500">{label}</span>
            <div className="text-right">
                <span data-testid={testid} className={`text-sm font-medium ${toneCls} ${mono ? "font-mono" : ""}`}>{value}</span>
                {sub && <span className="ml-2 text-xs text-slate-400">· {sub}</span>}
            </div>
        </div>
    );
}

function fmt(iso) {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString("da-DK"); } catch { return iso; }
}

function AddonRow({ label, unitPrice, value, onChange, testid }) {
    const v = Number(value || 0);
    return (
        <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
            <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800">{label}</p>
                <p className="text-xs text-slate-500">{new Intl.NumberFormat("da-DK").format(unitPrice)} kr./stk.</p>
            </div>
            <div className="flex items-center gap-1.5">
                <button type="button"
                    onClick={() => onChange(Math.max(0, v - 1))}
                    data-testid={`${testid}-minus`}
                    className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600">−</button>
                <input type="number" min="0" value={v}
                    onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || "0", 10)))}
                    data-testid={testid}
                    className="w-16 h-8 text-center rounded-lg border border-slate-200 font-mono text-sm" />
                <button type="button"
                    onClick={() => onChange(v + 1)}
                    data-testid={`${testid}-plus`}
                    className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600">+</button>
            </div>
        </div>
    );
}

function Row({ label, value, bold, valueClass = "", testid }) {
    return (
        <div className="flex justify-between items-center">
            <span className={`text-slate-600 ${bold ? "font-semibold" : ""}`}>{label}</span>
            <span data-testid={testid} className={`font-mono ${bold ? "font-bold text-slate-900" : ""} ${valueClass}`}>{value}</span>
        </div>
    );
}

// ---------- ElevenLabs Integration Card (mockup layout) ----------

function IntegrationChecklist({ el, companyId, onUpdated }) {
    const [saving, setSaving] = useState(false);
    const agent = Boolean(el?.agent_id_masked);
    const postCall = Boolean(el?.post_call_configured);
    const hmac = Boolean(el?.webhooks?.hmac_verified);
    const nativeOn = Boolean(el?.native_enabled);

    const toggleNative = async () => {
        if (!companyId) return;
        setSaving(true);
        try {
            await patchAdminCompanyNativeFlag(companyId, "elevenlabs", !nativeOn);
            toast.success(!nativeOn
                ? "Native ElevenLabs slået til for virksomheden"
                : "Native ElevenLabs slået fra for virksomheden");
            onUpdated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke opdatere flag");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="int-el-checklist">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">STATUS-TJEKLISTE</p>
            <ul className="space-y-2 text-sm">
                <ChecklistItem
                    ok={agent}
                    okText="Agent ID er udfyldt."
                    warnText="Agent ID mangler."
                    testid="chk-agent-id" />
                <ChecklistItem
                    ok={postCall}
                    okText="Post-Call secret er gemt."
                    warnText="Post-Call secret er ikke konfigureret."
                    testid="chk-post-call-secret" />
                <ChecklistItem
                    ok={nativeOn}
                    okText="Native ElevenLabs flag er slået til for denne virksomhed."
                    warnText="Native ElevenLabs flag er slået fra for denne virksomhed."
                    testid="chk-native-flag">
                    <NativeToggle enabled={nativeOn} saving={saving} onToggle={toggleNative} testid="chk-native-toggle" />
                </ChecklistItem>
                <ChecklistItem
                    ok={hmac}
                    okText="HMAC-signatur verificeret."
                    warnText="HMAC-signatur er ikke verificeret."
                    testid="chk-hmac" />
            </ul>
        </div>
    );
}

function ChecklistItem({ ok, okText, warnText, testid, children }) {
    return (
        <li className="flex items-center gap-2" data-testid={testid}>
            {ok ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            )}
            <span className={ok ? "text-slate-700" : "text-amber-800 font-medium"}>
                {ok ? okText : warnText}
            </span>
            {children && <span className="ml-auto">{children}</span>}
        </li>
    );
}

function NativeToggle({ enabled, saving, onToggle, testid }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            disabled={saving}
            data-testid={testid}
            aria-pressed={enabled}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition disabled:opacity-50 ${
                enabled ? "bg-emerald-500" : "bg-slate-300"
            }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition shadow ${
                    enabled ? "translate-x-4" : "translate-x-0.5"
                }`}
            />
        </button>
    );
}

function ElevenLabsIntegrationCard({ el, company, onReload }) {
    const [busy, setBusy] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const health = el?.health || "not_configured";
    const usage = el?.usage_month || {};
    const webhooks = el?.webhooks || {};
    const route = el?.route || {};
    const vi = webhooks.voice_intake || {};
    const pc = webhooks.post_call || {};
    const prec = webhooks.pre_call || {};

    const capBought = Number(el?.capacity_bought || 0);
    const capConfigured = Number(el?.capacity_configured || 0);
    const minutesUsed = Math.floor(Number(usage.used_seconds || 0) / 60);
    const minutesIncluded = Math.floor(Number(usage.included_seconds || 0) / 60);
    const pct = Math.round(Number(usage.pct || 0) * 100);
    const avgMinFmt = (() => {
        const s = Number(usage.avg_seconds_per_call || 0);
        if (!s) return "—";
        const m = Math.floor(s / 60);
        const rest = Math.round(s - m * 60);
        return `${m}:${String(rest).padStart(2, "0")} min.`;
    })();

    const manualAiEnabled = el?.manual_ai_enabled !== false;
    const currentlyBlocked = health === "blocked" || route.effective_route_mode === "human";

    const doBlock = async () => {
        setBusy(true);
        try {
            await manualBlockAdminIntegration(company?.id);
            toast.success("AI spærret manuelt");
            onReload?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke spærre"); }
        finally { setBusy(false); }
    };
    const doReopen = async () => {
        setBusy(true);
        try {
            await manualReopenAdminIntegration(company?.id);
            toast.success("AI genåbnet");
            onReload?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke genåbne"); }
        finally { setBusy(false); }
    };

    return (
        <div className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="int-el-card">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-600 text-white text-[10px] font-bold tracking-wider">11L</span>
                    <p className="text-sm font-bold uppercase tracking-wider text-slate-800">AI INTEGRATION</p>
                </div>
                <HealthPill health={health} />
            </div>

            {/* AGENT & TELEFON */}
            <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">AGENT OG TELEFON</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <MetaRow icon={PhoneCall} label="Agent" value={el?.agent_label || "—"} testid="int-el-agent-label" />
                    <MetaRow icon={Radio} label="Agent ID" value={el?.agent_id_masked || "—"} mono testid="int-el-agent-id" />
                    <MetaRow icon={Radio} label="Telefon" value={el?.phone_number || "—"} mono testid="int-el-phone" />
                    <MetaRow icon={Radio} label="Phone-ID" value={el?.phone_number_id || "—"} mono testid="int-el-phone-id" />
                    <MetaRow icon={CreditCard} label="Pakke" value={el?.package_label || "—"}
                        valueClass={el?.package_label ? "font-semibold text-blue-700" : "text-slate-400"}
                        testid="int-el-package" />
                    <MetaRow icon={ListChecks} label="Kapacitet"
                        value={`${capBought} købt · ${capConfigured} konfigureret`}
                        testid="int-el-capacity" />
                    <MetaRow icon={PhoneCall} label="Samtidige opkald"
                        value={`${el?.concurrent_calls_bought || 0} købt · ${el?.concurrent_calls_configured || 0} konfigureret`}
                        testid="int-el-concurrency" />
                    <MetaRow icon={Zap} label="Burst"
                        value={el?.burst_enabled ? "Aktiveret" : "Deaktiveret"}
                        valueClass={el?.burst_enabled ? "text-emerald-700" : "text-slate-500"}
                        testid="int-el-burst" />
                </div>
            </div>

            {/* FORBRUG */}
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60">
                <div className="flex items-baseline justify-between mb-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        FORBRUG – <span className="text-slate-700">{(usage.month_label || "").toUpperCase() || "—"}</span>
                    </p>
                    <span className="text-xs text-slate-500 tabular-nums" data-testid="int-el-usage-pct">{pct}%</span>
                </div>
                <div className="flex items-baseline justify-between mb-2">
                    <p className="text-base font-semibold text-slate-900 tabular-nums" data-testid="int-el-minutes">
                        {new Intl.NumberFormat("da-DK").format(minutesUsed)} / {new Intl.NumberFormat("da-DK").format(minutesIncluded)} minutter
                    </p>
                </div>
                <UsageBar pct={pct} status={usage.usage_status} />
                <div className="grid grid-cols-3 gap-2 mt-3 text-[11px] text-slate-500">
                    <span>Base: <b className="text-slate-700">{usage.base_minutes || 0} min</b></span>
                    <span>Månedlig extra: <b className="text-slate-700">{usage.monthly_extra_minutes || 0} min</b></span>
                    <span>Engangs extra: <b className="text-slate-700">{usage.one_time_minutes_active || 0} min</b></span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <UsageStat label="opkald" value={usage.calls_count || 0} testid="int-el-calls" />
                    <UsageStat label="jobs" value={usage.jobs_count || 0} testid="int-el-jobs" />
                    <UsageStat label="i gennemsnit" value={avgMinFmt} mono testid="int-el-avg" />
                </div>
            </div>

            {/* DRIFT */}
            <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">DRIFT</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <MetaRow icon={Activity} label="Effektiv route" value={route.effective_route_mode || "—"}
                        valueClass={route.effective_route_mode === "ai" ? "text-emerald-700 font-medium" : "text-amber-700 font-medium"}
                        testid="int-el-route" />
                    <MetaRow icon={Activity} label="Grund" value={route.decision_reason || "—"} testid="int-el-reason" />
                    <MetaRow icon={ShieldCheck} label="Manuel AI"
                        value={manualAiEnabled ? "Til" : "Fra"}
                        valueClass={manualAiEnabled ? "text-emerald-700" : "text-rose-700"}
                        testid="int-el-manual" />
                    <MetaRow icon={PhoneCall} label="Viderestilling"
                        value={el?.forward_phone || "— MANGLER"}
                        valueClass={el?.forward_phone ? "text-slate-800 font-mono" : "text-rose-700 font-medium"}
                        testid="int-el-forward" />
                </div>
                <div className="mt-4 space-y-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">WEBHOOKS</p>
                    <WebhookRow label="Pre-call" status={prec.status} lastAt={prec.last_event_at} configured={prec.configured} testid="int-el-webhook-pre" />
                    <WebhookRow label="Voice Intake" status={vi.status} lastAt={vi.last_event_at} configured={vi.configured} testid="int-el-webhook-vi" />
                    <WebhookRow label="Post-Call" status={pc.status} lastAt={pc.last_event_at} configured={pc.configured} testid="int-el-webhook-pc" />
                    <div className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-slate-600">HMAC</span>
                        <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${webhooks.hmac_verified ? "text-emerald-700" : "text-amber-700"}`} data-testid="int-el-hmac">
                            <Dot color={webhooks.hmac_verified ? "emerald" : "amber"} />
                            {webhooks.hmac_verified ? "Verificeret" : "Ikke verificeret"}
                        </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-slate-600">Fejl (24t)</span>
                        <span className={`text-sm font-mono font-semibold ${Number(webhooks.errors_24h) > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="int-el-errors">
                            {webhooks.errors_24h || 0}
                        </span>
                    </div>
                </div>
                {el?.last_integration_error && (
                    <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700" data-testid="int-el-error">
                        <AlertTriangle className="w-3 h-3 inline mr-1" /> Seneste fejl: {el.last_integration_error}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="px-5 py-3 bg-slate-50 flex flex-wrap gap-2">
                <ActionButton icon={Settings2} label="Kontrollér konfiguration" onClick={() => {
                    onReload?.();
                    toast.info("Status genindlæst");
                }} testid="int-el-btn-check" />
                <ActionButton icon={ExternalLink} label="Se opkald" onClick={() => {
                    const url = `/admin/opkald?company=${encodeURIComponent(company?.id || "")}`;
                    window.open(url, "_blank", "noopener");
                }} testid="int-el-btn-calls" />
                <ActionButton icon={Activity} label="Redigér integration" tone="ghost"
                    onClick={() => setEditOpen(true)} testid="int-el-btn-edit" />
                {currentlyBlocked || !manualAiEnabled ? (
                    <ActionButton icon={CheckCircle2} label="Genåbn" tone="success" disabled={busy}
                        onClick={doReopen} testid="int-el-btn-reopen" />
                ) : (
                    <ActionButton icon={AlertTriangle} label="Spær manuelt" tone="danger" disabled={busy}
                        onClick={doBlock} testid="int-el-btn-block" />
                )}
            </div>

            {editOpen && (
                <IntegrationEditDialog
                    el={el}
                    companyId={company?.id}
                    onClose={() => setEditOpen(false)}
                    onSaved={() => { setEditOpen(false); onReload?.(); }}
                />
            )}
        </div>
    );
}

function IntegrationEditDialog({ el, companyId, onClose, onSaved }) {
    const [form, setForm] = useState({
        agent_id: "",
        agent_label: el?.agent_label || "",
        phone_number: el?.phone_number || "",
        phone_number_id: el?.phone_number_id || "",
        forward_phone: el?.forward_phone || "",
        manual_ai_enabled: el?.manual_ai_enabled !== false,
        concurrent_calls_bought: el?.concurrent_calls_bought || 0,
        concurrent_calls_configured: el?.concurrent_calls_configured || 0,
        burst_enabled: !!el?.burst_enabled,
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        setSaving(true);
        try {
            const payload = {};
            for (const k of Object.keys(form)) {
                const v = form[k];
                if (v === "" || v === null || v === undefined) continue;
                payload[k] = v;
            }
            await patchAdminIntegrationElevenLabs(companyId, payload);
            toast.success("Integration opdateret");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Kunne ikke gemme");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="int-el-edit-dialog">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                    <h3 className="text-base font-bold text-slate-900">Redigér integration</h3>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
                </header>
                <div className="p-6 space-y-4 text-sm">
                    <Field label="Agent-label (visningsnavn)"><Input v={form.agent_label} on={(v) => set("agent_label", v)} test="edit-agent-label" /></Field>
                    <Field label="Agent ID (ElevenLabs)"><Input v={form.agent_id} on={(v) => set("agent_id", v)} test="edit-agent-id" placeholder="Lad være tom for at bevare" /></Field>
                    <Field label="Telefonnummer (visning)"><Input v={form.phone_number} on={(v) => set("phone_number", v)} test="edit-phone" /></Field>
                    <Field label="Phone Number ID (ElevenLabs)"><Input v={form.phone_number_id} on={(v) => set("phone_number_id", v)} test="edit-phone-id" /></Field>
                    <Field label="Viderestillingsnummer (fallback)"><Input v={form.forward_phone} on={(v) => set("forward_phone", v)} test="edit-forward" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Samtidige opkald (købt)"><NumInput v={form.concurrent_calls_bought} on={(v) => set("concurrent_calls_bought", v)} test="edit-cc-bought" /></Field>
                        <Field label="Samtidige opkald (konfig.)"><NumInput v={form.concurrent_calls_configured} on={(v) => set("concurrent_calls_configured", v)} test="edit-cc-cfg" /></Field>
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-slate-100">
                        <span className="text-slate-700">Manuel AI aktiv</span>
                        <NativeToggle enabled={form.manual_ai_enabled} onToggle={() => set("manual_ai_enabled", !form.manual_ai_enabled)} testid="edit-manual" />
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-slate-100">
                        <span className="text-slate-700">Burst-tilstand</span>
                        <NativeToggle enabled={form.burst_enabled} onToggle={() => set("burst_enabled", !form.burst_enabled)} testid="edit-burst" />
                    </div>
                </div>
                <footer className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100">Annullér</button>
                    <button onClick={submit} disabled={saving} data-testid="edit-save"
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Gem
                    </button>
                </footer>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs font-medium text-slate-600 block mb-1">{label}</span>
            {children}
        </label>
    );
}
function Input({ v, on, test, placeholder }) {
    return <input type="text" value={v} onChange={(e) => on(e.target.value)} data-testid={test}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />;
}
function NumInput({ v, on, test }) {
    return <input type="number" min="0" value={v} onChange={(e) => on(Number(e.target.value || 0))} data-testid={test}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />;
}

function HealthPill({ health }) {
    const map = {
        ok:              { txt: "Drift OK",           cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "emerald" },
        ready_for_test:  { txt: "Klar til test",      cls: "bg-blue-50 text-blue-700 border-blue-200",           dot: "blue" },
        warning:         { txt: "Advarsel",           cls: "bg-amber-50 text-amber-700 border-amber-200",        dot: "amber" },
        error:           { txt: "Fejl",               cls: "bg-rose-50 text-rose-700 border-rose-200",           dot: "rose" },
        blocked:         { txt: "Spærret",            cls: "bg-rose-50 text-rose-700 border-rose-300",           dot: "rose" },
        not_configured:  { txt: "Ikke konfigureret",  cls: "bg-slate-100 text-slate-600 border-slate-200",       dot: "slate" },
        idle:            { txt: "Inaktiv",            cls: "bg-slate-100 text-slate-600 border-slate-200",       dot: "slate" },
    };
    const m = map[health] || map.not_configured;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${m.cls}`} data-testid="int-el-health">
            <Dot color={m.dot} /> {m.txt}
        </span>
    );
}

function Dot({ color = "slate" }) {
    const map = {
        emerald: "bg-emerald-500", rose: "bg-rose-500", amber: "bg-amber-500", slate: "bg-slate-400", blue: "bg-blue-500",
    };
    return <span className={`inline-block w-2 h-2 rounded-full ${map[color] || map.slate}`} />;
}

function MetaRow({ icon: Icon, label, value, mono, valueClass = "", testid }) {
    return (
        <div className="flex items-center gap-2 min-w-0">
            {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
            <span className="text-xs text-slate-500 shrink-0">{label}:</span>
            <span data-testid={testid} className={`truncate text-sm text-slate-800 ${mono ? "font-mono" : ""} ${valueClass}`}>
                {value}
            </span>
        </div>
    );
}

function UsageBar({ pct, status }) {
    const p = Math.max(0, Math.min(100, Number(pct) || 0));
    const barCls = status === "blocked" || p >= 100 ? "bg-rose-500"
        : status === "warning" || p >= 80 ? "bg-amber-500"
        : "bg-blue-600";
    return (
        <div className="w-full h-2.5 rounded-full bg-slate-200 overflow-hidden" data-testid="int-el-usage-bar">
            <div className={`h-full ${barCls} transition-all`} style={{ width: `${p}%` }} />
        </div>
    );
}

function UsageStat({ label, value, mono, testid }) {
    return (
        <div className="rounded-xl bg-white border border-slate-200 py-2 px-2">
            <p data-testid={testid} className={`text-lg font-bold text-slate-900 ${mono ? "font-mono" : "tabular-nums"}`}>{value}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</p>
        </div>
    );
}

function WebhookRow({ label, status, lastAt, configured, testid }) {
    const map = {
        ok:    { txt: "OK",              color: "emerald", txtCls: "text-emerald-700" },
        error: { txt: "Fejl",            color: "rose",    txtCls: "text-rose-700"    },
        idle:  { txt: "Ingen trafik",    color: "slate",   txtCls: "text-slate-500"   },
        not_configured: { txt: "Ikke konfigureret", color: "amber", txtCls: "text-amber-700" },
    };
    const m = map[status] || (configured ? map.idle : map.not_configured);
    const t = fmtShort(lastAt);
    return (
        <div className="flex items-center justify-between py-1.5 text-sm" data-testid={testid}>
            <span className="text-slate-600">{label}</span>
            <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 font-medium ${m.txtCls}`}>
                    <Dot color={m.color} /> {m.txt}
                </span>
                <span className="text-xs text-slate-400 font-mono w-24 text-right">
                    {t ? `Senest ${t}` : "—"}
                </span>
            </div>
        </div>
    );
}

function ActionButton({ icon: Icon, label, onClick, tone = "solid", testid, disabled }) {
    const toneCls = {
        solid:   "bg-slate-900 hover:bg-slate-700 text-white border-slate-900",
        ghost:   "bg-white hover:bg-slate-100 text-slate-700 border-slate-200",
        success: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600",
        danger:  "bg-rose-600 hover:bg-rose-700 text-white border-rose-600",
    };
    const cls = toneCls[tone] || toneCls.solid;
    return (
        <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition disabled:opacity-50 ${cls}`}>
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label}
        </button>
    );
}

function fmtShort(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay) return d.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" });
        return d.toLocaleDateString("da-DK", { day: "2-digit", month: "2-digit" }) + " " +
               d.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return null;
    }
}
